import React, { useState, useEffect } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  Circle,
  useMapEvents,
  CircleMarker
} from 'react-leaflet';
import L from 'leaflet';
import tileLayer from '../util/tileLayer';
import LegendCreator from './LegendCreator';
import MapUpdater from './MapUpdater';
import LocationControl from './LocationControl';
import GoogleMapOverlay from './GoogleMapOverlay';
import { getDistance } from 'geolib';
//import { CircleMarker } from 'react-leaflet';

// Fallback center if geolocation fails – Ottawa
const defaultCenter = [45.4215, -75.6972];

// Child component to handle map clicks.
const MapClickHandler = ({ addMarker }) => {
  useMapEvents({
    click(e) {
      console.log("Map clicked at:", e.latlng);
      addMarker(e.latlng);
    }
  });
  return null;
};

function DistanceMap() {
  const [center, setCenter] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [totalDistance, setTotalDistance] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [zoneCenter, setZoneCenter] = useState(null);
  const zoneRadius = 20; // Radius in meters (adjust as needed)

  // Get the user's current location; fallback to defaultCenter.
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          setCenter([position.coords.latitude, position.coords.longitude]),
        (error) => {
          console.error("Error obtaining location:", error);
          setCenter(defaultCenter);
        }
      );
    } else {
      setCenter(defaultCenter);
    }
  }, []);

  // Function to clear the search address text
  const clearSearch = () => {
    setSearchQuery("");
  };

  // Recalculate cumulative distance when markers change.
  useEffect(() => {
    if (markers.length < 2) {
      setTotalDistance(0);
      return;
    }
    let cumulativeDistance = 0;
    for (let i = 1; i < markers.length; i++) {
      const pointA = L.latLng(markers[i - 1]);
      const pointB = L.latLng(markers[i]);
      {/*cumulativeDistance += pointA.distanceTo(pointB);*/}
      try {
              const distance = getDistance(
                { latitude: pointA.lat, longitude: pointA.lng },
                { latitude: pointB.lat, longitude: pointB.lng }
              );
              cumulativeDistance += distance
          } catch (error) {
            console.error("Distance calculation error:", error);
          }
    }

    // Change to feet if needed. (Currently using original kilometers conversion)
    // setTotalDistance((cumulativeDistance * 3.28084).toFixed(2));
    // For now, we'll stick with km conversion:
    setTotalDistance((cumulativeDistance * 3.28084).toFixed(2));
  }, [markers]);

  // Function to add a marker when the map is clicked.
  const addMarker = (latlng) => {
    setMarkers((prevMarkers) => [...prevMarkers, [latlng.lat, latlng.lng]]);
  };

  // Function to delete a marker based on its index.
  const handleDeleteMarker = (indexToDelete) => {
    setMarkers((prevMarkers) => prevMarkers.filter((_, index) => index !== indexToDelete));
  };

  // Clear button handler.
  const handleClear = () => {
    console.log("Clearing markers...");
    setMarkers([]);
  };

  // Submit button handler.
  const handleSubmit = async () => {
    if (markers.length < 2) {
      alert("At least 2 markers are required to submit the data.");
      return;
    }

    const payload = {
      markerNumbers: markers.map((_, idx) => idx + 1),
      markerCoordinates: markers,
      totalDistance: totalDistance,
      submittedAt: new Date().toISOString(),
      address: searchQuery
    };

    console.log("Submitting payload:", payload);

    try {
      const response = await fetch("/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      alert("Data successfully submitted to DynamoDB.");
    } catch (error) {
      console.error("Error submitting data:", error);
      alert("Submission failed. Please try again.");
    }
  };

  // Handle address search.
  const handleSearch = async (query) => {
    // Use the passed query if provided; otherwise, fall back to the state.
    const addressToSearch = query || searchQuery;
    if (!addressToSearch) return;

    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressToSearch)}`;
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data && data.length > 0) {
        const result = data[0];
        const newCenter = [parseFloat(result.lat), parseFloat(result.lon)];
        setCenter(newCenter);
        // Set the zone center to the new address coordinates
        setZoneCenter(newCenter);
      } else {
        alert("Address not found!");
      }
    } catch (error) {
      console.error("Error during address search:", error);
    }
  };

  // Wait until we have a center.
  if (!center) return <div>Loading map...</div>;

  // Function to handle POCI button click:
  const handlePOCI = async () => {
    try {
      const response = await fetch("/poci");
      if (response.ok) {
        const data = await response.json();
        const address = data.address;
        // Update the search bar
        setSearchQuery(address);
        // Directly call handleSearch with the new address value.
        handleSearch(address);
      } else {
        alert("Failed to retrieve a POCI address.");
      }
    } catch (error) {
      console.error("Error fetching POCI address:", error);
      alert("Error fetching POCI address. Please try again.");
    }
  };

  return (
    <>
      {/* Controls above the map */}
      <div style={{ margin: "10px" }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Enter address to search"
          style={{ marginRight: "5px", width: "250px" }}
        />
        <button onClick={() => handleSearch()}>Search Address</button>
        <button onClick={handleClear} style={{ marginLeft: "10px" }}>
          Clear Markers
        </button>
        <button onClick={handlePOCI} style={{ marginLeft: '10px' }}>POCI</button>
        <button onClick={handleSubmit} style={{ marginLeft: "10px" }}>
          Submit
        </button>
      </div>

      <MapContainer
        center={center}
        zoom={18}
        scrollWheelZoom={true}
        style={{ height: "80vh", width: "100%" }}
      >
        <MapUpdater center={center} zoom={18} />
        <TileLayer url={tileLayer.url} attribution={tileLayer.attribution} maxZoom={19} />
        <LocationControl onClearSearch={clearSearch}/>
        <MapClickHandler addMarker={addMarker} />
        <LegendCreator markers={markers} totalDistance={totalDistance} />

        {/* Add the Google Maps overlay */}
        <GoogleMapOverlay center={center} zoom={19} />

        {/* Render the zone circle if zoneCenter is defined */}
        {zoneCenter && (
          <Circle
            center={zoneCenter}
            radius={zoneRadius}
            pathOptions={{ color: 'red', fillColor: 'red', fillOpacity: 0.2 }}
            className="crosshair-cursor"
          />
        )}

        {/* Render markers with a popup that includes a delete button */}
        {/*{markers.map((position, index) => (
          <Marker key={index} position={position} draggable>
            <Popup>
              {`Marker ${index + 1}: (${position[0].toFixed(5)}, ${position[1].toFixed(5)})`}
              <br />
              <button onClick={() => handleDeleteMarker(index)}>Delete</button>
            </Popup>
          </Marker>
        ))}*/}
        {markers.map((position, index) => (
          <CircleMarker
            key={index}
            center={position}
            radius={1}
            pathOptions={{ color: 'black', fillColor: 'transparent', fillOpacity: 0 }}
          >
            <Popup>
              {`Marker ${index + 1}: (${position[0].toFixed(5)}, ${position[1].toFixed(5)})`}
              <br />
              <button onClick={() => handleDeleteMarker(index)}>Delete</button>
            </Popup>
          </CircleMarker>
        ))}



        {markers.length > 1 && <Polyline positions={markers} color="black" />}

        {markers.length > 0 && (
          <Marker position={markers[markers.length - 1]} opacity={0}>
            <Popup autoClose={false}>
              {`Total Distance: ${totalDistance} ft`}
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </>
  );
}

export default DistanceMap;